// © Microsoft Corporation. All rights reserved.

import { minimumHostPagePathSegments } from "../constants";

/**
 * The HostPage interface uses words defined by the Prime project for us to locate in what hostpage
 * the Feeds project is running.
 */
export interface IHostPage {
    originalUrl: string;    // ex: https://www.msn.com/spartan/ntp/interests/my?fdhead=gizmodemo
    verticalKey: string, // ex: spartan or compass
    categoryKey: string, // ex: dhp or ntp
    hostName: string, // ex: www.msn.com
    topDomain: string, // ex: msn.com or msn.cn
    subcategoryKey?: string // ex: interests or discover
}

export namespace HostPage {
    /**
     * This function extracts the hostPage information from the passed in href. This is based on the
     * Prime pathname convention of "/vertical/category/subcategory". If the passed href does not have at least the
     * category, then null is returned instead as this is considered an invalid URL.
     * @param {string} href - The URL, which will be used to extract the hostPage
     * @returns {IHostPage | null}
     */
    export function getHostPageFromUrl(href: string): IHostPage | null {
        if (!href) { return; }
        const url = new URL(href);

        const host = url.hostname;
        const pathnameSegments = url.pathname.split("/");

        // for cookie stamping that needs the msn.com or msn.cn path isntead of using the full host value
        const topDomainName = (host.match(/[^.]+\.[^.\d]+$/) || {})[0] || "";

        /**
         * The + 1 in the following statement is take into account the empty string "segment"
         * that would be to the left of the first backslash in a valid path like "/spartan/ntp"
         */
        if (pathnameSegments.length <= minimumHostPagePathSegments) {
            return {
                originalUrl: href,
                categoryKey: null,
                verticalKey: null,
                hostName: host,
                topDomain: topDomainName
            }; // When the user url doesn't have atleast two pathsegments the vertical and category keys are null
        }

        /**
         * Consider path segments that have market in it e.g.
         * /en-us/news/other/title/ar-cmsId
         * /en-us/video/animals
         * /en-us/sports/nfl/scores
         */
        const isMarketPresent = pathnameSegments[1].length === 5 && pathnameSegments[1].split("-").length === 2;
        const indexOffset = isMarketPresent ? 1 : 0;

        const hostPage: IHostPage = {
            originalUrl: href,
            categoryKey: pathnameSegments[2 + indexOffset],
            verticalKey: pathnameSegments[1 + indexOffset],
            hostName: host,
            topDomain: topDomainName
        };

        if (pathnameSegments.length >= minimumHostPagePathSegments) {
            hostPage.subcategoryKey = pathnameSegments[3 + indexOffset];
        }
        return hostPage;
    }
}